import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthGuard as Auth0Guard, AuthService } from '@auth0/auth0-angular';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard extends Auth0Guard {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {
    super(authService);
  }

  canLoad(): Observable<boolean> {
    // Auth0 redirects to the redirect_uri (e.g. /brand) with ?error=access_denied
    // BEFORE the SDK has a chance to process and clear the URL. Angular's
    // canLoad fires at this point — the URL is still intact — so we can detect
    // the error here and redirect to the unguarded /auth-error page.
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get('error');
    if (authError) {
      this.router.navigate(['/auth-error'], {
        queryParams: {
          error: authError,
          error_description: urlParams.get('error_description'),
        },
        replaceUrl: true,
      });
      return of(false);
    }

    return combineLatest([this.authService.isLoading$, this.authService.isAuthenticated$]).pipe(
      filter(([isLoading]) => !isLoading),
      take(1),
      tap(([, loggedIn]) => {
        if (!loggedIn) {
          this.authService.loginWithRedirect({ appState: { target: 'role-routing' } });
        }
      }),
      map(([, isAuthenticated]) => isAuthenticated),
    );
  }
}
